/*
 * Combined Products — switches the PDP to a sibling product without a reload.
 *
 * Every option is a real <a href="/products/handle">, so the picker works with
 * JS disabled and is crawlable. On click we intercept and use the Section
 * Rendering API instead:
 *
 *   1. fetch /products/<handle>?section_id=<this section>
 *   2. swap the innerHTML of #shopify-section-<id> with the response
 *   3. refresh every other section that carries data-update-section="<its id>"
 *      (Nährwerte, reviews, …) — each one is its own request, because
 *      ?section_id= only ever renders a single section
 *   4. pushState to the product URL, so Back returns to the previous product
 *
 * Section ids are resolved against the TARGET product's template, so all
 * products of a group must sit on a template where those ids exist.
 */

if (!customElements.get('combined-products')) {
  /*
   * Beim Sortenwechsel wird die Section neu gerendert, das Element also frisch
   * aufgebaut — ein Zustand am Element selbst waere danach weg. Deshalb liegt
   * "aufgeklappt" hier im Modul-Scope: ueberlebt jeden Swap, wird aber beim
   * Verlassen der PDP mit dem Skript verworfen. Startzustand bleibt zu.
   */
  let shopperExpanded = false;

  /*
   * Der Swap entfernt das Element aus dem DOM, disconnectedCallback bricht dabei
   * seinen AbortController ab. Die Nachladungen der verlinkten Sections starten
   * aber erst danach — mit dem Controller des Elements waeren sie also sofort
   * abgebrochen. Sie haengen deshalb an einem eigenen Controller hier im Modul,
   * den der naechste Wechsel abbricht.
   */
  let linkedController = null;

  function linkedSignal() {
    if (linkedController) linkedController.abort();
    linkedController = new AbortController();
    return linkedController.signal;
  }

  class CombinedProducts extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.handleClick.bind(this);
      this.onPopState = this.handlePopState.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.abortController = null;
      this.expanded = false;
      this.resizeRaf = null;
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('resize', this.onResize);
      this.initCollapse();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('resize', this.onResize);
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      if (this.abortController) this.abortController.abort();
    }

    get sectionId() {
      return this.dataset.section;
    }

    handleClick(event) {
      const toggle = event.target.closest('[data-combined-toggle]');
      if (toggle) {
        event.preventDefault();
        this.expanded = !this.expanded;
        shopperExpanded = this.expanded;
        this.applyCollapse();
        return;
      }

      const option = event.target.closest('[data-combined-option]');
      if (!option || option.hasAttribute('data-selected')) return;

      // Let the browser handle modified clicks (new tab, download, …).
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      const url = option.getAttribute('href');
      if (!url) return;

      event.preventDefault();
      this.switchTo(url, true);
    }

    handlePopState() {
      // Back/forward between products of the group: re-render for the URL we landed on.
      this.switchTo(window.location.pathname + window.location.search, false);
    }

    // --- Collapse / expand ----------------------------------------------------

    initCollapse() {
      this.toggleButton = this.querySelector('[data-combined-toggle]');
      this.toggleText = this.querySelector('[data-combined-toggle-text]');
      this.items = Array.from(this.querySelectorAll('[data-combined-option]'));
      this.tilesMobile = parseInt(this.dataset.tilesMobile, 10) || 2;
      this.tilesDesktop = parseInt(this.dataset.tilesDesktop, 10) || 4;
      this.showMoreText = this.dataset.showMore || '';
      this.showLessText = this.dataset.showLess || '';
      this.expanded = shopperExpanded;
      this.applyCollapse();
    }

    // Tiles per row for the current breakpoint. The last tile of a collapsed row
    // is the show-more toggle, so `tiles - 1` products stay visible.
    currentTiles() {
      return window.matchMedia('(min-width: 750px)').matches ? this.tilesDesktop : this.tilesMobile;
    }

    applyCollapse() {
      if (!this.toggleButton) return;

      const tiles = this.currentTiles();
      const total = this.items.length;

      // Everything fits in one row → no toggle, show every tile.
      if (total <= tiles) {
        this.toggleButton.removeAttribute('data-show');
        this.removeAttribute('data-collapsed');
        this.items.forEach((item) => item.removeAttribute('data-overflow'));
        return;
      }

      const visibleProducts = tiles - 1;
      const hidden = total - visibleProducts;
      this.toggleButton.setAttribute('data-show', '');

      if (this.expanded) {
        this.removeAttribute('data-collapsed');
        this.items.forEach((item) => item.removeAttribute('data-overflow'));
        if (this.toggleText) this.toggleText.textContent = this.showLessText;
        return;
      }

      this.setAttribute('data-collapsed', '');
      this.items.forEach((item, index) => {
        item.toggleAttribute('data-overflow', index >= visibleProducts);
      });

      // Keep the current product visible even if it sits in the hidden range:
      // reveal it and hide the last otherwise-visible tile in its place.
      const selectedIndex = this.items.findIndex((item) => item.hasAttribute('data-selected'));
      if (selectedIndex >= visibleProducts) {
        this.items[selectedIndex].removeAttribute('data-overflow');
        this.items[visibleProducts - 1].setAttribute('data-overflow', '');
      }

      if (this.toggleText) this.toggleText.textContent = `+${hidden} ${this.showMoreText}`;
    }

    handleResize() {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => this.applyCollapse());
    }

    async switchTo(url, updateUrl) {
      const section = document.getElementById(`shopify-section-${this.sectionId}`);
      if (!section) return;

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.toggleAttribute('data-loading', true);

      // Hoehe des aktuell gerenderten Loox-Balkens merken, bevor er weg ist.
      const ratingHeight = measureRating(section);

      try {
        const incoming = await this.fetchSection(url, this.sectionId, signal);
        if (!incoming) return;

        // From here on `this` is detached — the element lives inside the markup
        // we are about to replace, so never touch it again after the swap.
        section.innerHTML = incoming.innerHTML;

        reserveRating(section, ratingHeight);

        this.updateLinkedSections(url, linkedSignal());

        if (updateUrl) window.history.pushState({}, '', url);

        reinitAppEmbeds();
        document.dispatchEvent(new CustomEvent('combined-products:switched', { detail: { url } }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Fall back to a normal navigation so the shopper never gets stuck.
        console.error('[combined-products] switch failed, navigating instead', error);
        window.location.href = url;
      }
    }

    // Refresh every section that opted in via data-update-section, so the parts of
    // the page that depend on the product (nutritional values, reviews, …) follow along.
    updateLinkedSections(url, signal) {
      document.querySelectorAll('[data-update-section]').forEach(async (element) => {
        const sectionId = element.getAttribute('data-update-section');
        const target = document.getElementById(`shopify-section-${sectionId}`);
        if (!sectionId || !target || sectionId === this.sectionId) return;

        try {
          const incoming = await this.fetchSection(url, sectionId, signal);
          if (!incoming) return;
          target.innerHTML = incoming.innerHTML;
          // Nachgeladene Sections koennen selbst App-Widgets enthalten (Reviews),
          // die sonst leer bleiben — die Widgets kennen das neue Markup nicht.
          reinitAppEmbeds();
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error(`[combined-products] could not refresh section ${sectionId}`, error);
          }
        }
      });
    }

    async fetchSection(url, sectionId, signal) {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', sectionId);

      const response = await fetch(target.toString(), { credentials: 'same-origin', signal });
      if (!response.ok) throw new Error(`${response.status} for section ${sectionId}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      return html.getElementById(`shopify-section-${sectionId}`);
    }
  }

  /*
   * Der Loox-Sternebalken kommt als leerer Platzhalter aus dem Theme und wird
   * erst von der App gefuellt. Nach dem Swap ist er also kurz leer und alles
   * darunter rutscht hoch. Deshalb die zuletzt sichtbare Hoehe messen und im
   * frischen Markup reservieren, bis Loox nachgerendert hat.
   */
  // Der Link ums Widget kommt vom Theme und bleibt beim Rendern unangetastet —
  // daran laesst sich die Hoehe zuverlaessiger festmachen als am Platzhalter.
  function ratingHost(root) {
    return root.querySelector('a[href="#looxReviewsFrame"]') || root.querySelector('.loox-rating');
  }

  function measureRating(section) {
    const host = ratingHost(section);
    return host ? host.getBoundingClientRect().height : 0;
  }

  function reserveRating(section, height) {
    if (!height) return;

    const host = ratingHost(section);
    if (!host) return;

    host.style.minHeight = `${height}px`;

    // Sobald Loox das Widget gefuellt hat, traegt der Inhalt die Hoehe selbst.
    const observer = new MutationObserver(() => {
      const rating = host.querySelector('.loox-rating') || host;
      if (!rating.childElementCount) return;
      observer.disconnect();
      host.style.minHeight = '';
    });
    observer.observe(host, { childList: true, subtree: true });
  }

  // App widgets bind on page load, so they have to be told about the new DOM.
  function reinitAppEmbeds() {
    if (window.loox && typeof window.loox.init === 'function') window.loox.init();
    if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
  }

  customElements.define('combined-products', CombinedProducts);
}
